import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { clearSessionIndicator, getSessionIndicator, setSessionIndicator, setSignedOutIndicator } from "@/lib/session-indicator";
import { detectPortal } from "@/lib/portal";
import { nativeAuthRedirect } from "@/lib/native";

type Profile = {
  id: string;
  role: string;
  full_name: string | null;
  phone: string | null;
  email: string;
  status: string;
  avatar_url: string | null;
  last_login: string | null;
  created_at: string;
  updated_at: string;
};

type AuthContextType = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  profileError: string | null;
  mfaNeedsChallenge: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, metadata?: Record<string, string>) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [mfaNeedsChallenge, setMfaNeedsChallenge] = useState(false);
  // Track the most recent user ID to discard stale async responses
  const activeUserIdRef = useRef<string | null>(null);

  const checkMfaLevel = async () => {
    const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    if (!data) return;
    setMfaNeedsChallenge(data.nextLevel !== data.currentLevel);
  };

  const fetchProfile = async (userId: string) => {
    setProfileError(null);

    // Safety net: if the DB query hangs, unblock the UI after 10 s
    const timeout = setTimeout(() => {
      if (activeUserIdRef.current === userId) {
        setProfileError("Failed to load your account profile. Please refresh or sign in again.");
        setLoading(false);
      }
    }, 10_000);

    const { data, error } = await supabase
      .from("profiles")
      .select("id, role, full_name, phone, email, status, avatar_url, last_login, created_at, updated_at")
      .eq("id", userId)
      .single();

    clearTimeout(timeout);

    // Discard result if user has already signed out or switched accounts
    if (activeUserIdRef.current !== userId) return;

    if (error || !data) {
      setProfileError("Failed to load your account profile. Please refresh or sign in again.");
      setLoading(false);
      return;
    }
    setProfile(data as Profile);
    // The session indicator cookie is set explicitly at login time
    // (in LoginPage.handleSubmit). We deliberately do NOT re-set it here —
    // doing so would let a session-restore on a stale tab re-claim a cookie
    // that another portal owns, undermining cross-portal logout.
    setLoading(false);
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          activeUserIdRef.current = session.user.id;
          // Check MFA requirement (non-blocking — doesn't delay profile load)
          checkMfaLevel();
          // setTimeout avoids a Supabase client deadlock on the initial event
          setTimeout(() => fetchProfile(session.user.id), 0);
        } else {
          activeUserIdRef.current = null;
          setProfile(null);
          setProfileError(null);
          setMfaNeedsChallenge(false);
          setLoading(false);
        }
      }
    );

    // THEN check existing session — covers page refresh with an active session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        activeUserIdRef.current = session.user.id;
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    }).catch(() => { setLoading(false); });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (!error) {
      // Set indicator immediately — before onAuthStateChange fires — so ProtectedRoute
      // never sees user-set + null-indicator during the profile-load window.
      const here = detectPortal();
      if (here) setSessionIndicator(here);
      // Update last_login timestamp
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        supabase.from("profiles").update({ last_login: new Date().toISOString() }).eq("id", currentUser.id)
          .then(({ error }) => { if (error) console.error("last_login update failed:", error.message); })
          .catch(() => {});
      }
    }
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, metadata?: Record<string, string>) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata,
        // On native, reopen the app via its custom scheme; on web, same-origin.
        emailRedirectTo: nativeAuthRedirect("login-callback") ?? window.location.origin,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Only touch the indicator if we own it (or it's null/signed_out).
    // If another portal just took over (indicator = their ID), leave it so their
    // session claim stays intact — we're being evicted, not logging out.
    const here = detectPortal();
    const indicator = getSessionIndicator();
    if (indicator === null || indicator === "signed_out" || indicator === here) {
      setSignedOutIndicator();
    }
    try {
      await supabase.auth.signOut();
    } catch (e) {
      // Known supabase-js race: the auth-token Web Lock can be stolen by a
      // concurrent refresh (common on mobile tab resume). Local sign-out
      // still proceeds; server-side token revocation is best-effort.
      console.warn("supabase.auth.signOut failed:", e);
    }
    setSession(null);
    setUser(null);
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ session, user, profile, loading, profileError, mfaNeedsChallenge, signIn, signUp, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Role-based redirect mapping
// eslint-disable-next-line react-refresh/only-export-components
export const getRoleRedirect = (role: string): string => {
  const redirects: Record<string, string> = {
    super_admin: "/app/admin/dashboard",
    compliance: "/app/compliance/dashboard",
    corporate_admin: "/app/corporate/dashboard",
    corporate_finance: "/app/corporate/reports",
    corporate_hr: "/app/corporate/recipients",
    corporate_viewer: "/app/corporate/dashboard",
    corporate_user: "/app/corporate/vouchers",
    merchant_admin: "/app/merchant/dashboard",
    merchant_staff: "/app/merchant/redeem",
    agent: "/app/agent/dashboard",
    consumer: "/app/consumer/vouchers",
  };
  return redirects[role] || "/app/consumer/vouchers";
};
